import { useEffect, useState } from "preact/hooks";
import type { StepProps } from "../app";
import { recommend, submit, type AdderOption, type RecommendResult, type TierCard } from "../lib/api";
import { track } from "../lib/analytics";
import { ContactExpert } from "../ContactExpert";
import { TOS_URL, PRIVACY_URL } from "../lib/env";

export function Step4Recommendation({ state, update, next, back, stepName }: StepProps) {
  const [rec, setRec] = useState<RecommendResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    recommend(state)
      .then((r) => {
        if (cancelled) return;
        setRec(r);
        const recommended = r.tiers.find((t) => t.recommended) ?? r.tiers[0];
        update({ selectedTier: recommended.tier });
      })
      .catch(() => !cancelled && setError("We couldn't generate a recommendation. Please try again."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const money = (n: number) => `$${n.toLocaleString()}`;
  const qtyOf = (id: string) => state.selectedAdders[id] ?? 0;
  const setQty = (id: string, qty: number) =>
    update({ selectedAdders: { ...state.selectedAdders, [id]: Math.max(0, qty) } });

  // GA4 `select_item` — captures which package the customer leans toward, even
  // if they never check out. Feeds product-level retargeting audiences.
  const selectTier = (t: TierCard) => {
    update({ selectedTier: t.tier });
    track("select_item", {
      submission_id: state.submissionId,
      items: [{ item_id: t.sku, item_name: t.name, item_category: t.tier, price: t.priceTotal }],
    });
  };

  const selected: TierCard | undefined = rec?.tiers.find((t) => t.tier === state.selectedTier);
  const tierDisposition = selected?.disposition ?? "checkout";

  // Adder tallies.
  const adders = rec?.adders ?? [];
  const firmAdderTotal = adders
    .filter((a) => a.pricing === "fixed")
    .reduce((sum, a) => sum + a.unitPrice * qtyOf(a.id), 0);
  const hasEstimateSelected = adders.some((a) => a.pricing === "estimate" && qtyOf(a.id) > 0);

  // A site-estimate adder can't be firm-priced online -> gate like the lanes.
  const disposition =
    tierDisposition === "sales"
      ? "sales"
      : tierDisposition === "assessment" || hasEstimateSelected
        ? "assessment"
        : "checkout";

  const autoAdderTotal = (rec?.autoAdders ?? []).reduce((s, a) => s + a.lineTotal, 0);
  const grandTotal = (selected?.priceTotal ?? 0) + firmAdderTotal + autoAdderTotal;

  const selectionsForSubmit = () =>
    Object.entries(state.selectedAdders)
      .filter(([, q]) => q > 0)
      .map(([id, qty]) => ({ id, qty }));

  // E-sign consent must be an affirmative, timestamped user action (ESIGN/UETA,
  // NY GBL). Checking the box stamps the moment; unchecking clears it so we never
  // send stale consent.
  const setEsignConsent = (checked: boolean) =>
    update({ esignConsent: checked, esignConsentAt: checked ? new Date().toISOString() : null });

  const onConfirm = async () => {
    if (!state.selectedTier || disposition !== "checkout" || !state.esignConsent) return;
    setSubmitting(true);
    setError(null);
    // GA4 `begin_checkout` — checkout intent with the firm online total. The
    // authoritative revenue `purchase` is fired server-side once signed.
    track("begin_checkout", {
      submission_id: state.submissionId,
      value: grandTotal,
      currency: "USD",
      items: selected
        ? [{ item_id: selected.sku, item_name: selected.name, item_category: selected.tier, price: selected.priceTotal, quantity: 1 }]
        : [],
    });
    try {
      await submit({
        submissionId: state.submissionId,
        selectedTier: state.selectedTier,
        selectedAdders: selectionsForSubmit(),
        membershipOptIn: state.membershipOptIn,
        esignConsent: state.esignConsent,
        esignConsentAt: state.esignConsentAt,
      });
      next();
    } catch {
      setError("Something went wrong sending your contract. Please try again or contact us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <section class="step" role="status" aria-live="polite"><h2>Sizing your system…</h2><p class="hint">Analyzing your home and bill.</p></section>;
  if (error && !rec) return <section class="step"><p class="error" role="alert">{error}</p><button onClick={back}>Back</button></section>;
  if (!rec) return null;

  const renderAdder = (a: AdderOption) => {
    const q = qtyOf(a.id);
    const estimate = a.pricing === "estimate";
    return (
      <div class={`adder ${q > 0 ? "on" : ""}`} key={a.id}>
        <div class="adder-main">
          {a.quantityBased ? (
            <div class="adder-qty">
              <span class="stepper" role="group" aria-label={`${a.name} quantity`}>
                <button type="button" aria-label={`Remove one ${a.name}`} disabled={q <= 0} onClick={() => setQty(a.id, q - 1)}>−</button>
                <span class="count" aria-live="polite">{q}</span>
                <button type="button" aria-label={`Add one ${a.name}`} disabled={q >= (a.maxQty ?? 99)} onClick={() => setQty(a.id, q + 1)}>+</button>
              </span>
              <span>{a.name}</span>
            </div>
          ) : (
            <label class="check">
              <input type="checkbox" checked={q > 0} onChange={(e) => setQty(a.id, e.currentTarget.checked ? 1 : 0)} />
              {a.name}
            </label>
          )}
          <span class="adder-price">
            {money(a.unitPrice)}{a.quantityBased ? `/${a.unitLabel ?? "ea"}` : ""}
          </span>
        </div>
        {estimate && (
          <p class="adder-note">Priced after a site assessment — not part of the online total.</p>
        )}
        {!estimate && a.note && <p class="adder-note">{a.note}</p>}
      </div>
    );
  };

  return (
    <section class="step">
      <h2>Your recommendation</h2>
      <p class="blurb">{rec.recommendationBlurb}</p>

      {rec.baseIncludes.length > 0 && (
        <div class="includes">
          <span class="includes-title">Every install includes</span>
          <ul>{rec.baseIncludes.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}

      <div class="tiers" role="radiogroup" aria-label="Choose your package">
        {rec.tiers.map((t) => (
          <label key={t.tier} class={`tier ${state.selectedTier === t.tier ? "selected" : ""}`}>
            <input type="radio" name="tier" checked={state.selectedTier === t.tier} onChange={() => selectTier(t)} />
            <div class="tier-body">
              <div class="tier-head">
                <span class="tier-name">{t.name}</span>
                {t.recommended && <span class="badge">Recommended</span>}
              </div>
              <p class="tier-desc">{t.description}</p>
              <p class="tier-unit">{t.unitName} · {t.kw} kW on {rec.generatorFuel === "natural_gas" ? "natural gas" : "propane"}</p>
              <p class="tier-price">{money(t.priceTotal)}</p>
              {t.boxUpsizeDeltaUsd > 0 && <p class="tier-line">Includes a larger unit (+{money(t.boxUpsizeDeltaUsd)}) to carry your load.</p>}
              {t.gasLineAllowanceUsd > 0 && <p class="tier-line">+ {money(t.gasLineAllowanceUsd)} {rec.gasWorkLabel} — estimate, trued up after your site assessment.</p>}
              {t.disposition === "assessment" && <p class="tier-flag">Needs a quick site assessment before we can finalize pricing.</p>}
              {t.disposition === "sales" && <p class="tier-flag">Our team will design this one with you — checkout isn't available online.</p>}
            </div>
          </label>
        ))}
      </div>

      {adders.length > 0 && (
        <div class="adders">
          <h3>Add-ons <span class="optional">(optional)</span></h3>
          {adders.map(renderAdder)}
        </div>
      )}

      {rec.autoAdders.length > 0 && (
        <div class="auto-adders">
          <span class="auto-adders-title">Added based on your home</span>
          {rec.autoAdders.map((a) => (
            <div class="auto-adder" key={a.id}>
              <span>{a.name}{a.qty > 1 ? ` ×${a.qty}` : ""}</span>
              <span class="adder-price">{money(a.lineTotal)}</span>
            </div>
          ))}
        </div>
      )}

      {rec.membership && (
        <label class={`membership ${state.membershipOptIn ? "on" : ""}`}>
          <input
            type="checkbox"
            checked={state.membershipOptIn}
            onChange={(e) => update({ membershipOptIn: e.currentTarget.checked })}
          />
          <div class="membership-body">
            <div class="membership-head">
              <span class="membership-name">Become a {rec.membership.name} member</span>
              <span class="membership-price">
                {money(rec.membership.annualPriceUsd)}/yr
                {rec.membership.firstYearIncluded ? " · first year included" : ""}
              </span>
            </div>
            {rec.membership.tagline && <p class="membership-tag">{rec.membership.tagline}</p>}
            <ul class="membership-benefits">
              {rec.membership.benefits.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <p class="membership-terms">
              Billed annually, separate from this install; auto-renews. Terms apply.
            </p>
          </div>
        </label>
      )}

      <div class="total-row">
        <span>{hasEstimateSelected ? "Estimated total (before site items)" : "Your total"}</span>
        <strong>{money(grandTotal)}</strong>
      </div>

      {disposition === "checkout" && (
        <p class="consent-note">
          A {money(selected?.depositAmount ?? 500)} engineering fee is collected when you sign.
          {selected && selected.gasLineAllowanceUsd > 0 ? ` The ${rec.gasWorkLabel.toLowerCase()} line is an estimate, reconciled after your site assessment.` : ""}{" "}
          Continuing sends your agreement via DocuSign for e-signature.
        </p>
      )}
      {disposition === "assessment" && (
        <p class="consent-note">
          {hasEstimateSelected
            ? "One of your add-ons (site work) has to be priced after an on-site assessment, so we'll confirm the full price with you before signing."
            : "This configuration needs an on-site electrical/gas assessment before we can lock the install price."}{" "}
          Submit your details and our team will schedule it.
        </p>
      )}
      {disposition === "sales" && (
        <p class="consent-note">This job needs a custom design. Submit your details and a specialist will reach out.</p>
      )}
      {selected && selected.laneReasons.length > 0 && (
        <ul class="lane-reasons">{selected.laneReasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
      )}
      {disposition === "checkout" && (
        <label class="check consent esign-consent">
          <input
            type="checkbox"
            checked={state.esignConsent}
            onChange={(e) => setEsignConsent(e.currentTarget.checked)}
          />
          <span>
            I have read and agree to the{" "}
            <a href={TOS_URL} target="_blank" rel="noopener noreferrer">Terms of Service</a> and{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>, and I
            consent to sign my agreement electronically and to receive contract records in electronic
            form (E-SIGN / UETA).
          </span>
        </label>
      )}

      {error && <p class="error" role="alert">{error}</p>}

      <div class="actions">
        <button onClick={back} disabled={submitting}>Back</button>
        <ContactExpert stepName={stepName} submissionId={state.submissionId} />
        {disposition === "checkout" ? (
          <button class="primary" onClick={onConfirm} disabled={!state.selectedTier || submitting || !state.esignConsent}>
            {submitting ? "Sending…" : "Confirm & send my contract"}
          </button>
        ) : (
          <button class="primary" onClick={back} disabled={submitting}>Adjust my details</button>
        )}
      </div>
    </section>
  );
}
